import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import { type MenuOption } from '@saviynt/design-system';

import {
  ADVANCED_FILTERS_METHODS,
  DEFAULT_AD_FILTER_PARAMS,
  DEFAULT_FILTERS,
  VIOLATIONS_TAB_LIST_AD_FILTER,
} from '../../../shared/constants';
import { getPersonlization, useTypedSelector } from '../../../shared/hooks';
import {
  type ActiveFiltersState,
  type AdvancedFilterState,
} from '../../../shared/types';
import { violationsTabActions } from '../data-store/violationsTabSlice';

import { useFilterCache } from './use-filtercache';
import { useSetFilterDelta } from './use-setfiltersdelta';

/**
 * Interface representing a Promise that can be unwrapped
 * @template T The type of value the promise resolves to
 */
interface UnwrappablePromise<T> extends Promise<T> {
  unwrap(): Promise<T>;
}

/**
 * Type definition for the query map object
 * Maps filter keys to their corresponding query functions
 */
type QueryMapType = Record<
  string,
  (params: typeof DEFAULT_AD_FILTER_PARAMS) => UnwrappablePromise<unknown>
>;

/**
 * Interface representing a query item with its key, function and parameters
 */
interface QueryItem {
  key: string;
  fn: (params: typeof DEFAULT_AD_FILTER_PARAMS) => UnwrappablePromise<unknown>;
  params: typeof DEFAULT_AD_FILTER_PARAMS;
}

/**
 * Interface for mapping query responses to success and error states
 */
interface ResponseMap {
  success: Record<string, unknown>;
  errors: Record<string, unknown>;
}

/**
 * Custom hook for initializing and managing advanced filters
 * @param {QueryMapType} queryMap - Map of query functions for each filter
 * @param {function} l - Translation function that takes a key and returns localized string
 * @returns {Object} Object containing the initializeAdvancedFilters function
 *
 * @example
 * const { initializeAdvancedFilters } = useFiltersInitialization(
 *   queryMap,
 *   translateFunction
 * );
 */
const useFiltersInitialization = (
  queryMap: QueryMapType,
  l: (key: string) => string
) => {
  const dispatch = useDispatch();

  const { filterCache, filters } = useTypedSelector(
    (state) => state.aag.violationsTab.controls
  );

  const { setGroupInitialCache } = useFilterCache(
    filterCache,
    violationsTabActions.setFiltersCache
  );

  const { setDelta } = useSetFilterDelta();

  const savedFilters = getPersonlization()?.filters ?? null;

  const initializeAdvancedFilters = useCallback(async (): Promise<boolean> => {
    const queryArr: QueryItem[] = Object.entries(queryMap).map(
      ([value, fn]) => ({
        key: value,
        fn,
        params: DEFAULT_AD_FILTER_PARAMS,
      })
    );

    try {
      const results = await Promise.allSettled(
        queryArr.map(({ params: params_2, fn }) => fn(params_2).unwrap())
      );
      // Mapping response to success and error
      const responseMap = results.reduce<ResponseMap>(
        (acc, result_1, index) => {
          const { key } = queryArr[index];

          if (result_1.status === 'fulfilled') {
            acc.success[key] = result_1.value;

            return acc;
          }

          acc.errors[key] = result_1.reason;

          return acc;
        },
        { success: {}, errors: {} }
      );

      // If any filter api fails then throw exception
      const erroredFilters = Object.keys(responseMap.errors);

      if (erroredFilters.length > 0) {
        throw Error(`Error in fetching filter(s) ${erroredFilters.join('')}`);
      }

      const cacheObjects: Record<string, MenuOption[]> = {};
      const savedObjects: ActiveFiltersState[] = [];

      // Configuring filters
      const initialFilters: AdvancedFilterState[] = Object.values(
        VIOLATIONS_TAB_LIST_AD_FILTER
      ).map((filter) => {
        const options = [];
        let isDefaultFilter = Boolean(
          DEFAULT_FILTERS.includes(filter.value) ||
            filters.find((_filter) => _filter.value === filter.value)?.isChecked
        );
        const isOptionFromApi =
          filter.method === ADVANCED_FILTERS_METHODS.API_WITH_OPTIONS &&
          responseMap.success[filter.value];
        const isFixedOption =
          filter.method === ADVANCED_FILTERS_METHODS.CUSTOM ||
          filter.method === ADVANCED_FILTERS_METHODS.FIXED;
        const isPersonalized = Boolean(
          savedFilters !== null && Array.isArray(savedFilters[filter.value])
        );

        if (isOptionFromApi) {
          const successResponse = responseMap.success[
            filter.value
          ] as MenuOption[];

          options.push(...successResponse);

          cacheObjects[filter.value] = successResponse;
        }

        if (isFixedOption && filter.options) {
          const localizedOptions = filter.options.map((option) => ({
            ...option,
            label: l(option.label),
          }));

          options.push(...localizedOptions);
        }

        if (isPersonalized && savedFilters?.[filter.value]) {
          const savedOptions = savedFilters[filter.value];
          const optionMap = new Map(
            options.map((option) => [option.value, option])
          );

          savedOptions.forEach(([value, label]) => {
            const existing = optionMap.get(value);

            if (existing) {
              existing.isChecked = true;
            } else {
              optionMap.set(value, { label, value, isChecked: true });
            }
          });
          const activeOptions = Array.from(optionMap.values()).filter(
            (option) => option.isChecked
          );

          savedObjects.push({
            value: filter.value,
            method: filter.method,
            options: activeOptions,
          });

          isDefaultFilter = true;
        }

        return {
          label: l(filter.label),
          value: filter.value,
          isDisabled: false,
          isChecked: isDefaultFilter,
          method: filter.method,
          category: filter.category,
          options,
        };
      });

      // Personalized options
      setDelta([], savedObjects);

      // Initial cache
      setGroupInitialCache(cacheObjects, DEFAULT_AD_FILTER_PARAMS.search);

      // Filters configuration
      dispatch(violationsTabActions.setAdvancedFilters(initialFilters));

      return true;
    } catch (error) {
      Logger.log('Error fetching filters', error);

      return false;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- should be only one time for `defaultFilters`
  }, [queryMap, dispatch, l, setGroupInitialCache, filters, setDelta]);

  return { initializeAdvancedFilters };
};

// eslint-disable-next-line import/prefer-default-export
export { useFiltersInitialization };
